/*
 * HyperCrate landing-page plasma blob.
 *
 * Web port of the app's audition "Blob" visualizer (qt-prototype/qml/vizblob.frag):
 * a single organic metaball whose edge wobbles by a sum of band-weighted angular
 * sinusoids. The app drives the five band weights from live audio; here they are
 * replaced by low-frequency oscillators whose frequencies are integer multiples of
 * one loop phase, so the animation loops seamlessly (default 14 s) with no audio.
 * On top of the app shader it adds a glassy treatment: a rotating gradient using
 * the app's own spectral-centroid colour ramp (WaveformItem::hueForCentroid,
 * red-orange -> azure), a soft glow halo, a lit glass rim, and a gloss hotspot.
 *
 * Usage (see index.html; note: if you inline this file in a <script> tag instead of
 * loading it via src, a literal closing script tag anywhere in it — even in a comment —
 * would terminate the tag early, so this comment deliberately avoids writing one):
 *   const blob = createPlasmaBlob(document.getElementById('blob'));
 *
 * Renders premultiplied-alpha over a transparent canvas, so it composites onto
 * any page background. Returns { destroy() }.
 */
(function () {
  'use strict';

  var VERT = [
    'attribute vec2 a_pos;',
    'varying vec2 v_uv;',
    'void main() {',
    '  v_uv = a_pos * 0.5 + 0.5;',
    '  gl_Position = vec4(a_pos, 0.0, 1.0);',
    '}'
  ].join('\n');

  var FRAG = [
    'precision highp float;',
    'varying vec2 v_uv;',
    'uniform float u_phase;   // 0..2pi, one seamless loop',
    'uniform float u_aspect;',
    'uniform vec3 u_ptr;      // xy: unit direction to the pointer (blob space), z: influence 0..1',
    'uniform float u_pulse;   // tap pulse 0..1, decays in JS',
    '',
    'const float TAU = 6.28318530718;',
    '',
    'vec3 hsv2rgb(vec3 c) {',
    '  vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);',
    '  vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);',
    '  return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);',
    '}',
    '',
    '// The app-icon rainbow: the spectral ramp\'s warm->cool sweep, widened to the',
    '// icon\'s span — coral (just below red) through yellow/green/cyan to periwinkle.',
    'vec3 iconRamp(float t) {',
    '  float h = fract(1.0 + mix(-0.005, 0.66, clamp(t, 0.0, 1.0)));',
    '  return hsv2rgb(vec3(h, 0.78, 1.0));',
    '}',
    '',
    'void main() {',
    '  vec2 p = (v_uv - 0.5) * 2.0;',
    '  p.x *= u_aspect;',
    '  float ang = atan(p.y, p.x);',
    '  float d = length(p);',
    '',
    '  // Looping stand-ins for the app\'s live audio bands b0..b4 —',
    '  // every time term is an integer multiple of u_phase, so t=0 and t=loop match exactly.',
    '  float b0 = 0.5 + 0.5 * sin(u_phase * 1.0 + 0.0);',
    '  float b1 = 0.5 + 0.5 * sin(u_phase * 2.0 + 1.7);',
    '  float b2 = 0.5 + 0.5 * sin(u_phase * 3.0 + 3.1);',
    '  float b3 = 0.5 + 0.5 * sin(u_phase * 2.0 + 4.2);',
    '  float b4 = 0.5 + 0.5 * sin(u_phase * 1.0 + 5.3);',
    '',
    '  // per-band angular wobble, same harmonic ladder as vizblob.frag',
    '  float wob = b0 * sin(ang * 2.0 + u_phase * 1.0)',
    '            + b1 * sin(ang * 3.0 - u_phase * 2.0)',
    '            + b2 * sin(ang * 4.0 + u_phase * 1.0)',
    '            + b3 * sin(ang * 5.0 - u_phase * 3.0)',
    '            + b4 * sin(ang * 6.0 + u_phase * 2.0);',
    '',
    '  // pointer lean: a low-harmonic bulge toward the cursor, like a band-weight',
    '  // nudge in the app — subtle, and zero when the pointer is far away',
    '  float pang = atan(u_ptr.y, u_ptr.x);',
    '  wob += u_ptr.z * (1.3 * cos(ang - pang) + 0.45 * cos(2.0 * (ang - pang)));',
    '',
    '  float R = 0.60 + 0.045 * wob + 0.012 * sin(u_phase * 2.0 + 0.6) + 0.05 * u_pulse; // gentle breath + tap pulse',
    '',
    '  float inside = smoothstep(R + 0.004, R - 0.03, d);',
    '  float g = max(d - R, 0.0);',
    '  float glow = exp(-g * g * 22.0) * (1.0 - inside);   // soft halo, dies out before the mask',
    '  float q = (d - R) / 0.018;',
    '  float rim = exp(-q * q);                            // glossy shell at the edge',
    '',
    '  // spectral gradient across a diagonal that rotates once per loop: the same',
    '  // warm->cool "brightness" sweep the app paints waveforms with, gentle swirl kept',
    '  // away from the centre (ang is singular there and would pinch the hue)',
    '  vec2 dir = vec2(cos(u_phase + 2.4), sin(u_phase + 2.4));',
    '  float grad = dot(p, dir) / max(R, 1e-3);            // ~-1..1 across the body',
    '  float swirl = sin(ang * 3.0 + u_phase * 2.0) * smoothstep(0.2, 0.6, d);',
    '  vec3 col = iconRamp(0.5 + 0.5 * grad + 0.06 * swirl);',
    '',
    '  // the icon\'s magenta pocket: a local blush on the warm side\'s lower rim,',
    '  // riding the gradient rotation so it loops with everything else',
    '  float ma = u_phase + 2.4 + 3.14159 + 0.9;',
    '  vec2 mp = vec2(cos(ma), sin(ma)) * R * 0.8;',
    '  float mAmt = exp(-dot(p - mp, p - mp) * 9.0);',
    '  col = mix(col, hsv2rgb(vec3(0.87, 0.55, 1.0)), mAmt * 0.7);',
    '',
    '  // glass shading: fixed key light from the top, fresnel-lightened thick edge,',
    '  // an inner refraction contour, and a rim that brightens toward the light',
    '  vec2 n = p / max(d, 1e-4);                          // radial pseudo-normal',
    '  vec2 L = normalize(vec2(-0.35, 0.9));',
    '  float lit = 0.45 + 0.55 * max(dot(n, L), 0.0);',
    '  float edgeT = smoothstep(R - 0.34, R, d);           // 0 centre -> 1 at edge',
    '  vec3 body = mix(col, vec3(1.0), 0.12 - 0.08 * edgeT); // luminous centre, colour pools at edge',
    '  body = mix(body, vec3(1.0), pow(edgeT, 3.0) * 0.16 * lit); // fresnel roll-off',
    '  float fold = (d - (R - 0.10)) / 0.026;',
    '  body += vec3(1.0) * exp(-fold * fold) * 0.06 * lit; // inner refraction fold line',
    '',
    '  vec3 c = body * inside;',
    '  c += mix(col, vec3(1.0), 0.15) * glow * 0.06;       // colour-bleed halo, whisper-subtle',
    '  float rimLit = 0.10 + 0.30 * pow(max(dot(n, L), 0.0), 2.0);',
    '  c += vec3(1.0) * rim * rimLit;                      // glass rim, bright on the lit side',
    '',
    '  // glossy highlight orbiting once per loop: tight core + wide soft bloom',
    '  vec2 hp = vec2(cos(u_phase + 4.0), sin(u_phase + 4.0)) * R * 0.45;',
    '  vec2 hv = p - hp;',
    '  float r2 = dot(hv, hv);',
    '  c += (vec3(1.0) * exp(-r2 * 130.0) * 0.35',
    '      + mix(col, vec3(1.0, 0.72, 0.92), 0.55) * exp(-r2 * 11.0) * 0.18) * inside; // pink-fringed gloss',
    '',
    '  float a = clamp(inside + glow * 0.06 + rim * 0.3 * rimLit, 0.0, 1.0);',
    '',
    '  // circular falloff to fully transparent well inside the canvas bounds,',
    '  // so the square crop of the canvas never shows against the page',
    '  float mask = 1.0 - smoothstep(0.84, 0.985, d);',
    '  gl_FragColor = vec4(c * mask, a * mask);            // premultiplied alpha',
    '}'
  ].join('\n');

  function compile(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      throw new Error('plasma-blob shader: ' + gl.getShaderInfoLog(s));
    }
    return s;
  }

  function createPlasmaBlob(canvas, opts) {
    opts = opts || {};
    var loopSeconds = opts.loopSeconds || 14;

    var gl = canvas.getContext('webgl', {
      alpha: true,
      premultipliedAlpha: true,
      antialias: true
    });
    if (!gl) return null; // no WebGL: leave the canvas empty (page background shows)

    var prog = gl.createProgram();
    gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      throw new Error('plasma-blob link: ' + gl.getProgramInfoLog(prog));
    }
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW); // fullscreen tri
    var loc = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    var uPhase = gl.getUniformLocation(prog, 'u_phase');
    var uAspect = gl.getUniformLocation(prog, 'u_aspect');
    var uPtr = gl.getUniformLocation(prog, 'u_ptr');
    var uPulse = gl.getUniformLocation(prog, 'u_pulse');
    gl.uniform3f(uPtr, 1, 0, 0);
    gl.uniform1f(uPulse, 0);

    // Layout reads (clientWidth, getBoundingClientRect) force a synchronous reflow, so
    // they must not run per frame. ResizeObserver + a passive scroll listener mark the
    // cached rect dirty instead; a still page does zero layout reads while animating.
    var rect = null, rectDirty = true;
    // 30fps: the loop is a 14s organic wobble, so half the frames are indistinguishable
    // and cost half the main-thread + GPU time during page load.
    var minFrameMs = 1000 / 30, lastDraw = -1e9;

    function resize() {
      // Cap at 1.5x rather than 2x: on a soft organic gradient the extra pixels of a
      // full 2x buffer are imperceptible but cost ~78% more fragment shading.
      var dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      var w = Math.max(1, Math.round(canvas.clientWidth * dpr));
      var h = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      gl.viewport(0, 0, w, h);
      gl.uniform1f(uAspect, w / h);
      rectDirty = true;
    }

    var reduced = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // pointer state: raw client coords captured in the (cheap) move handler,
    // influence direction/strength smoothed per frame inside the render loop
    var ptrCX = 0, ptrCY = 0, hasPtr = false;
    var ptrX = 1, ptrY = 0, ptrS = 0, pulse = 0;
    function onPtrMove(e) { ptrCX = e.clientX; ptrCY = e.clientY; hasPtr = true; }
    function onPtrGone() { hasPtr = false; }
    function onScroll() { rectDirty = true; }
    function onPtrDown() { if (!external) pulse = 1; }
    if (!reduced) {
      window.addEventListener('pointermove', onPtrMove, { passive: true });
      window.addEventListener('scroll', onScroll, { passive: true });
      document.addEventListener('pointerleave', onPtrGone);
      canvas.addEventListener('pointerdown', onPtrDown, { passive: true });
    }

    // External clock override (setClock on the returned handle): lets a caller
    // drive the phase from its own timebase — the VSL player uses it to converge
    // the live blob onto the rendered intro blob's phase (renderAt(4.6 + t) in
    // hypercrate-logo-intro) so the crossfade into the video is shape-exact.
    // While external, pointer lean and tap pulses are suppressed: the rendered
    // blob has neither, and they would break the match.
    var external = false, phaseFn = null;

    var raf = 0, running = false;
    function frame(now) {
      if (!reduced && running) raf = requestAnimationFrame(frame);
      if (!reduced && now - lastDraw < minFrameMs) return;
      lastDraw = now;
      var sec = now / 1000;
      if (phaseFn) sec = phaseFn(sec);
      var t = reduced ? 0 : ((sec % loopSeconds) + loopSeconds) % loopSeconds; // frozen frame if reduced motion
      gl.uniform1f(uPhase, (t / loopSeconds) * Math.PI * 2);
      if (external) hasPtr = false;
      if (!reduced) {
        if (rectDirty || !rect) { rect = canvas.getBoundingClientRect(); rectDirty = false; }
        var rx = rect.width / 2, ry = rect.height / 2;
        var dx = ptrX, dy = ptrY, target = 0;
        if (hasPtr && rx > 0) {
          dx = (ptrCX - (rect.left + rx)) / rx;
          dy = -(ptrCY - (rect.top + ry)) / ry;   // GL space: y up
          var dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > 1e-4) { dx /= dist; dy /= dist; } else { dx = ptrX; dy = ptrY; }
          target = Math.max(0, 1 - dist / 2.4) * 0.85; // full near the blob, gone by ~2.4 radii
        }
        ptrS += (target - ptrS) * 0.07;
        ptrX += (dx - ptrX) * 0.09;
        ptrY += (dy - ptrY) * 0.09;
        pulse *= 0.94;
        gl.uniform3f(uPtr, ptrX, ptrY, ptrS);
        gl.uniform1f(uPulse, pulse);
      }
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    // render only while on screen and the tab is visible — the loop is pure
    // decoration, so off-screen frames are wasted GPU/battery (77% of traffic
    // is mobile in-app browsers). Phase is wall-clock, so resume is seamless.
    var inView = true;
    function sync() {
      var should = inView && !document.hidden;
      if (should && !running) {
        running = true;
        raf = requestAnimationFrame(frame);
      } else if (!should && running) {
        running = false;
        cancelAnimationFrame(raf);
      }
    }
    function onVis() { sync(); }
    document.addEventListener('visibilitychange', onVis);
    var io = null;
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(function (entries) {
        inView = entries[entries.length - 1].isIntersecting;
        sync();
      }, { rootMargin: '80px' });
      io.observe(canvas);
    }
    // Paint one frame straight away so the hero is never blank, but hold the animation
    // loop until load + idle. Animating through page load competes with every other
    // script for the one main thread, which is exactly what Total Blocking Time measures.
    resize();
    frame(0);
    if (!reduced) {
      var start = function () { sync(); };
      var whenIdle = function () {
        if (window.requestIdleCallback) window.requestIdleCallback(start, { timeout: 1500 });
        else setTimeout(start, 200);
      };
      if (document.readyState === 'complete') whenIdle();
      else window.addEventListener('load', whenIdle, { once: true });
    }

    var ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(resize);
      ro.observe(canvas);
    }

    return {
      setClock: function (fn) { external = !!fn; phaseFn = fn || null; },
      destroy: function () {
        running = false;
        cancelAnimationFrame(raf);
        if (ro) ro.disconnect();
        if (io) io.disconnect();
        document.removeEventListener('visibilitychange', onVis);
        window.removeEventListener('pointermove', onPtrMove);
        window.removeEventListener('scroll', onScroll);
        document.removeEventListener('pointerleave', onPtrGone);
        canvas.removeEventListener('pointerdown', onPtrDown);
        var ext = gl.getExtension('WEBGL_lose_context');
        if (ext) ext.loseContext();
      }
    };
  }

  window.createPlasmaBlob = createPlasmaBlob;
})();
